import React, {useState} from "react"
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	Box,
	Typography,
	Modal,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import MaterialTable from "@material-table/core"
import {LineItem} from "../../../../dtos/orderResponse"
import {Carousel, Card, FlexBox} from "@vp/swan"
import SwanConfiguration from "../../design/SwanConfiguration"
import {PreviewModes} from "@vp/react-product-preview"
import {Event} from "../../../../dtos/eventResponse"
import {getAccessToken} from "../../../../utils/auth"
import {PurcsPreview} from "@vp/react-product-preview"
import {postActionLog, ButtonActionTypes} from "../../../../services/order/TrackOrderActions"
import {getCaseId} from "../../../../utils/UrlParser"
import {v4 as uuidv4} from "uuid"
import {useDocumentEditHistory} from "./UseDocumentEditHistory"
import CompareModal from "./CompareDocumentVersion/CompareModal"
import {ModifiedEvent} from "./types"
import {makeStyles} from "tss-react/mui"

const caseId = getCaseId()

interface DocumentEditProps {
	itemKeyEvents?: Event[]
	locale: string
	productKey: string
	optionSelection: Object
	previewInstructionsUri: string
	lineItem: LineItem
	updateLineItem: () => void
	orderNumber: string
	documentId: string | null
}
const useStyle = makeStyles()({
	previewImage: {
		width: "100%",
		height: "auto",
		objectFit: "contain",
	},
})
const DocumentEditHistory: React.FC<DocumentEditProps> = ({
	itemKeyEvents,
	locale,
	productKey,
	previewInstructionsUri,
	optionSelection,
	lineItem,
	updateLineItem,
	orderNumber,
	documentId,
}) => {
	const {classes} = useStyle()
	const [open, setOpen] = useState(false)
	const [compareOpen, setCompareOpen] = useState(false)
	const [previewOpen, setPreviewOpen] = useState(false)
	const [currentPreviewData, setCurrentPreviewData] = useState<ModifiedEvent | null>(null)
	const {events, latestVersion} = useDocumentEditHistory(documentId, itemKeyEvents)
	const handlePreviewOpen = (event: ModifiedEvent) => {
		setCurrentPreviewData(event)
		setPreviewOpen(true)
	}

	const handlePreviewClose = () => {
		setPreviewOpen(false)
		setCurrentPreviewData(null)
	}
	const handleClick = () => {
		postActionLog(
			{
				event: ButtonActionTypes.ClickDocumentEditHistoryButton,
				actionMetaData: {
					buttonClickId: uuidv4(),
					orderNumber,
					lineItemId: lineItem.id,
					documentId,
				},
			},
			caseId
		)
		setOpen(true)
	}

	return (
		<Box sx={{p: 2}}>
			<Button
				variant="contained"
				color="primary"
				size="small"
				onClick={handleClick}
				disabled={events.length === 0}
				sx={{fontWeight: 600}}
			>
				Document Edit History
			</Button>
			<Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
				<DialogTitle>
					<Typography variant="h6">Document Edit History</Typography>
					<Box
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							display: "flex",
							gap: 1,
							alignItems: "center",
						}}
					>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setCompareOpen(true)}
							disabled={events.length === 0 || documentId === undefined}
						>
							Compare
						</Button>
						<IconButton
							aria-label="close"
							onClick={() => setOpen(false)}
							sx={{color: (theme) => theme.palette.grey[500]}}
						>
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>

				<DialogContent dividers>
					<MaterialTable<ModifiedEvent>
						columns={[
							{
								title: "Date",
								field: "date",
								render: (rowData) => new Date(rowData?.date).toString(),
							},
							{
								title: "Preview",
								field: "docrefURL",
								render: (rowData) => (
									<Button
										variant="text"
										onClick={() => handlePreviewOpen(rowData)}
										style={{color: "#1976d2", textTransform: "none"}}
									>
										Show Preview
									</Button>
								),
							},
							{title: "Changed By", field: "owner"},
						]}
						data={events}
						options={{paging: false, search: false, toolbar: false}}
					/>
				</DialogContent>
			</Dialog>
			<Modal open={previewOpen} onClose={handlePreviewClose}>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: "60%",
						overflowY: "auto",
						maxHeight: "80%",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 2,
					}}
				>
					{currentPreviewData?.owner === "Doc Review" && (
						<SwanConfiguration>
							<Carousel
								aria-label="Carousel Preview of the document"
								accessibleTextForPrevious="Go to previous document"
								accessibleTextForNext="Go to next document"
								aria-roledescription="carouselPreviews"
								skin="full"
								slidesToShow={1}
							>
								{(currentPreviewData.docrefURL as string[]).map((link, index) => (
									<Card bordered key={index}>
										<FlexBox flexDirection="row">
											<img src={link} alt="Preview" className={classes.previewImage} />
										</FlexBox>
									</Card>
								))}
							</Carousel>
						</SwanConfiguration>
					)}

					{currentPreviewData?.owner === "Care Specialist" && (
						<PurcsPreview
							purcsOrigin="https://lookup.previews.cimpress.io"
							requestor="care-console"
							previewMode={PreviewModes.Carousel}
							bearerToken={getAccessToken()}
							docRefURL={currentPreviewData.docrefURL}
							locale={locale}
							coreProductKey={productKey}
							optionSelections={optionSelection}
							previewInstructionsUri={previewInstructionsUri}
						/>
					)}
				</Box>
			</Modal>
			<CompareModal
				open={compareOpen}
				onClose={() => setCompareOpen(false)}
				orderNumber={orderNumber}
				lineItem={lineItem}
				reloadParent={updateLineItem}
				events={events}
				latestVersion={latestVersion}
				documentId={documentId}
				locale={locale}
				productKey={productKey}
				optionSelection={optionSelection}
				previewInstructionsUri={previewInstructionsUri}
			/>
		</Box>
	)
}

export default DocumentEditHistory
